import { createContext } from 'preact';
import { useCallback, useContext, useMemo, useState } from 'preact/hooks';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { html } from '@shared/utils/rendering/html';

const AdminContext = createContext();

export function AdminContextProvider({ children }) {
    const queryClient = useQueryClient();

    const [cacheKeys, setCacheKeys] = useState({
        userTable: [],
        systemTable: [],
    });

    const addCacheKey = useCallback((table, key) => {
        if (!table) return;
        setCacheKeys((prev) => ({
            ...prev,
            [table]: prev[table]?.includes(key) ? prev[table] : [...(prev[table] ?? []), key],
        }));
    }, []);

    const removeCacheKey = useCallback((table, key) => {
        setCacheKeys((prev) => ({
            ...prev,
            [table]: prev[table].filter((k) => k !== key),
        }));
    }, []);

    const upsertOne = (oldData, updated) => {
        if (!Array.isArray(oldData)) return updated;
        const idx = oldData.findIndex((k) => k?.keyId === updated?.keyId);
        if (idx === -1) return [updated, ...oldData];
        const copy = oldData.slice();
        copy[idx] = updated;
        return copy;
    };

    const makeOnSuccessUpdater = (transform, cacheKey) => (response) => {
        if (!cacheKey || !response) return;
        queryClient.setQueryData(cacheKey, (old) => transform(old, response));
    };

    const updateSystemMutation = useMutation({
        mutationFn: (payload) => payload,
        onSuccess: makeOnSuccessUpdater(upsertOne, cacheKeys.systemTable),
        onSettled: () =>
            cacheKeys.systemTable &&
            queryClient.invalidateQueries({ queryKey: cacheKeys.systemTable }),
    });

    const updateUserMutation = useMutation({
        mutationFn: (payload) => payload,
        onSuccess: makeOnSuccessUpdater(upsertOne, cacheKeys.userTable),
        onSettled: () =>
            cacheKeys.userTable && queryClient.invalidateQueries({ queryKey: cacheKeys.userTable }),
    });

    const handleUserUpdate = useCallback(
        (payload) => {
            if (cacheKeys.userTable.length > 0) {
                updateUserMutation.mutate(payload);
            }
        },
        [updateUserMutation]
    );

    const handleSystemUpdate = useCallback(
        (payload) => {
            if (cacheKeys.systemTable.length > 0) {
                updateSystemMutation.mutate(payload);
            }
        },
        [updateSystemMutation]
    );

    const contextValue = useMemo(
        () => ({
            addCacheKey,
            removeCacheKey,
            handleUserUpdate,
            handleSystemUpdate,
        }),
        [addCacheKey, removeCacheKey, handleUserUpdate, handleSystemUpdate]
    );

    return html`<${AdminContext.Provider} value=${contextValue}>${children}</${AdminContext.Provider}>`;
}

export function useAdminContext() {
    return useContext(AdminContext);
}
